import { isEmpty } from 'lodash'
import axios from 'axios'
import md5 from 'md5'

/**
 * Generates an avatar properties object based on the provided email and optional background color.
 * The properties are used to create the image by passing this to the generateAvatar function
 * @param email
 */
export const generateAvatarInitial = (email: string) => {
  if (!email) throw new Error('Email is required')
  if (isEmpty(email.trim())) throw new Error('Email is empty')
  const myNames = email?.split('@')?.shift()?.split('.')
  const initials = myNames?.shift()?.charAt(0) + (myNames?.pop()?.charAt(0) ?? '')
  return initials.toUpperCase()
}

/**
 * Generates an avatar image URL based on the provided email and optional background color.
 * This function cannot be tested because it relies on the HTML canvas API.
 * @param {string} email - The email address to generate the avatar for.
 * @param {string} backgroundColor The background color for the avatar.
 * @param {string} textColor The text color for the avatar.
 * @returns {string} The data URL of the generated avatar image.
 */
export const generateAvatar = (email: string, backgroundColor: string, textColor: string): string => {
  if (!email) throw new Error('Email is required')
  if (!backgroundColor) throw new Error('Background color is required')
  if (!textColor) throw new Error('Text color is required')
  const nameInitials = generateAvatarInitial(email)
  return generateAvatarImage(nameInitials, textColor, backgroundColor)
}

/**
 * Get profile avatar from gravatar
 * If gravatar not found, generate avatar from email.
 * @param email
 * @param backgroundColor {string} are needed to generate avatar if gravatar not found
 * @param textColor {string} are needed to generate avatar if gravatar not found
 */
export const getProfileAvatar = async (email: string, backgroundColor: string, textColor: string) => {
  const hash = md5(email)
  try {
    const avatar = await axios({
      baseURL: `https://www.gravatar.com/avatar/${hash}?d=404`,
      method: 'get',
    })

    if (avatar.status === 200) {
      return `https://www.gravatar.com/avatar/${hash}`
    }
  } catch (e) {
    return generateAvatar(email, backgroundColor, textColor)
  }
}

/**
 * Determines the appropriate text color (black or white) based on the background color's luminance.
 * @param {string} hexColor - The hex color code of the background color.
 * @returns {string} 'black' if the background color is light, 'white' if it is dark.
 */
export const getContrastYIQ = (hexColor: string): 'black' | 'white' => {
  if (hexColor.includes('#')) {
    hexColor = hexColor.slice(1)
  }
  const r = parseInt(hexColor.substr(0, 2), 16)
  const g = parseInt(hexColor.substr(2, 2), 16)
  const b = parseInt(hexColor.substr(4, 2), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 128 ? 'black' : 'white'
}

/**
 * Generates a data URL for an avatar image with the given text, foreground color, and background color.
 * @param {string} text - The text to display on the avatar.
 * @param {string} foregroundColor - The color of the text.
 * @param {string} backgroundColor - The background color of the avatar.
 * @returns {string} The data URL of the generated avatar image.
 */
const generateAvatarImage = (text: string, foregroundColor: string, backgroundColor: string): string => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context) {
    return ''
  }

  canvas.width = 200
  canvas.height = 200

  context.fillStyle = backgroundColor
  context.fillRect(0, 0, canvas.width, canvas.height)

  context.fillStyle = foregroundColor
  context.font = '100px Inter'
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillText(text, canvas.width / 2, (canvas.height + 12) / 2)
  return canvas.toDataURL('image/png')
}
